import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import DOMPurify from "dompurify"
import { Marked } from "marked"
import { stableMarkdownBoundary } from "../utils/stable_markdown_boundary"

// Dedicated instance so streaming options never leak into other marked users
// (markdown_preview_controller configures the global marked singleton).
const markdownRenderer = new Marked({ breaks: true, gfm: true })
const RECONCILE_INTERVAL_MS = 2000
const RECONCILE_MAX_INTERVAL_MS = 15000
const RECONCILE_MAX_DURATION_MS = 15 * 60 * 1000
const TURBO_STREAM_MIME_TYPE = "text/vnd.turbo-stream.html"

/**
 * Message Stream Controller
 *
 * Renders the live tail of a streaming assistant answer client-side. The server
 * appends raw text chunks (via Turbo Stream append) into hidden buffer elements;
 * this controller observes them, strips artifacts the server decorators would
 * remove (<references>, <questions>, [[ACTIONS]] markers), renders the text
 * buffer as markdown and the reasoning buffer as plain text, and hides the
 * processing spinner once content is visible. The final canonical render is
 * still done server-side when the answer completes.
 *
 * Usage (rendered by message_answers/_streaming.html.erb):
 *   <div class="assistant--message" data-controller="message-stream">
 *     <div class="assistant--message-content">
 *       <div data-message-stream-target="reasoningSection" hidden>
 *         <div data-message-stream-target="reasoningOutput"></div>
 *       </div>
 *       <div data-message-stream-target="textOutput"></div>
 *       <div data-message-stream-target="spinner">...</div>
 *     </div>
 *     <div data-message-stream-target="textBuffer" hidden></div>
 *     <div data-message-stream-target="reasoningBuffer" hidden></div>
 *   </div>
 *
 * @value reconcileUrl URL used to recover terminal state when Cable misses it
 * @value reconcileInterval Polling interval in milliseconds
 * @value reconcileMaxInterval Maximum polling interval after exponential backoff
 * @value reconcileMaxDuration Maximum automatic polling duration
 *
 * The buffers live outside .assistant--message-content so the auto-linker and
 * clipboard controllers never touch the raw markdown source.
 */
export default class extends Controller {
  static targets = ["textBuffer", "reasoningBuffer", "textOutput", "reasoningOutput", "reasoningSection", "spinner"]
  static values = {
    reconcileUrl: String,
    reconcileInterval: { type: Number, default: RECONCILE_INTERVAL_MS },
    reconcileMaxInterval: { type: Number, default: RECONCILE_MAX_INTERVAL_MS },
    reconcileMaxDuration: { type: Number, default: RECONCILE_MAX_DURATION_MS },
    allowedTags: Array,
    allowedAttributes: Array
  }

  connect() {
    this._stableSource = ""
    this._tailNodes = []
    this._connected = true
    this._terminalReceived = false
    this._reconcileStartedAt = Date.now()
    this._reconcileAttempts = 0
    this._onConnectivityRestored = () => this._reconcile({ force: true })
    this._onVisibilityChange = () => {
      if (!document.hidden) this._reconcile({ force: true })
    }

    window.addEventListener("focus", this._onConnectivityRestored)
    window.addEventListener("online", this._onConnectivityRestored)
    document.addEventListener("visibilitychange", this._onVisibilityChange)
    this._scheduleReconciliation()
  }

  textBufferTargetConnected(element) {
    this._observe(element)
  }

  reasoningBufferTargetConnected(element) {
    this._observe(element)
  }

  disconnect() {
    this._connected = false
    this._observers().forEach((observer) => observer.disconnect())
    this._observers().length = 0
    clearTimeout(this._renderTimer)
    clearTimeout(this._reconcileTimer)
    this._reconcileAbortController?.abort()
    window.removeEventListener("focus", this._onConnectivityRestored)
    window.removeEventListener("online", this._onConnectivityRestored)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    this._renderScheduled = false
  }

  // Private: watch a buffer for appended chunks and render on every change
  _observe(element) {
    const observer = new MutationObserver(() => this._scheduleRender())
    observer.observe(element, { childList: true, subtree: true, characterData: true })
    this._observers().push(observer)
    this._scheduleRender()
  }

  // Private: collapse bursts of mutations into a single render per tick
  _scheduleRender() {
    if (this._renderScheduled) return

    this._renderScheduled = true
    this._renderTimer = setTimeout(() => {
      this._renderScheduled = false
      this._render()
    }, 0)
  }

  _render() {
    const text = this._cleanText(this.hasTextBufferTarget ? this.textBufferTarget.textContent : "")
    const reasoning = (this.hasReasoningBufferTarget ? this.reasoningBufferTarget.textContent : "").trim()
    const hasText = text.trim() !== ""

    if (this.hasTextOutputTarget) {
      if (hasText) {
        this._renderMarkdown(text)
      } else {
        this._resetMarkdown()
      }
    }

    if (reasoning !== "" && this.hasReasoningSectionTarget && this.hasReasoningOutputTarget) {
      this.reasoningSectionTarget.hidden = false
      this.reasoningOutputTarget.textContent = reasoning
    }

    if ((hasText || reasoning !== "") && this.hasSpinnerTarget) {
      this.spinnerTarget.hidden = true
    }
  }

  _renderMarkdown(text) {
    if (this._stableSource === "" || !text.startsWith(this._stableSource)) this._resetMarkdown()

    const boundary = stableMarkdownBoundary(text, this._stableSource.length)
    if (boundary < this._stableSource.length) this._resetMarkdown()
    this._removeMarkdownTail()
    if (boundary > this._stableSource.length) {
      this._appendMarkdown(text.slice(this._stableSource.length, boundary))
      this._stableSource = text.slice(0, boundary)
    }
    this._tailNodes = this._appendMarkdown(text.slice(boundary))
  }

  _appendMarkdown(source) {
    const template = document.createElement("template")
    template.innerHTML = DOMPurify.sanitize(markdownRenderer.parse(source), {
      ALLOWED_TAGS: this.allowedTagsValue,
      ALLOWED_ATTR: this.allowedAttributesValue,
      FORBID_ATTR: ["class"],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false
    })
    template.content.querySelectorAll("a").forEach((link) => { link.target = "_blank" })
    const nodes = Array.from(template.content.childNodes)
    this.textOutputTarget.append(template.content)
    return nodes
  }

  _removeMarkdownTail() {
    this._tailNodes.forEach((node) => node.remove())
    this._tailNodes = []
  }

  _resetMarkdown() {
    this.textOutputTarget.innerHTML = ""
    this._stableSource = ""
    this._tailNodes = []
  }

  // Private: mirror the server-side decorator cleanup for the streamed tail.
  // References/questions blocks are rendered separately by the server. Action
  // card markers are stripped here. Trailing partial fragments are hidden until
  // the next chunk completes them.
  //
  // Only leading newlines are dropped, not leading spaces or tabs: an answer
  // opening with an indented code block must reach marked with its indentation
  // so it renders as pre/code, the same way Commonmarker renders it at finalize.
  _cleanText(text) {
    return text
      .replace(/^\nRespuesta:/, "")
      .replace(/<references>[\s\S]*?<\/references>/g, "")
      .replace(/<references>[\s\S]*$/, "")
      .replace(/<questions>[\s\S]*?<\/questions>/g, "")
      .replace(/<questions>[\s\S]*$/, "")
      .replace(/\[\[ACTIONS(?::\d+(?:-\d+)?)?\]\]/g, "")
      .replace(/<\/?[a-zA-Z][\w-]*$/, "")
      .replace(/\[\[[^\]]*$/, "")
      .replace(/^\n+|\s+$/g, "")
  }

  // Private: schedule an HTTP fallback because Action Cable broadcasts are
  // online-only and cannot replay a missed terminal answer.
  _scheduleReconciliation() {
    if (!this.hasReconcileUrlValue || this._terminalReceived || !this._connected) return
    if (this._reconciliationExpired()) return

    clearTimeout(this._reconcileTimer)
    this._reconcileTimer = setTimeout(() => this._reconcile(), this._reconciliationDelay())
  }

  async _reconcile({ force = false } = {}) {
    if (!this.hasReconcileUrlValue || this._terminalReceived || this._reconcileInFlight) return
    if (!force && this._reconciliationExpired()) return

    clearTimeout(this._reconcileTimer)
    this._reconcileInFlight = true
    this._reconcileAbortController = new AbortController()

    try {
      const response = await fetch(this.reconcileUrlValue, {
        headers: { Accept: TURBO_STREAM_MIME_TYPE, "X-Background-Request": "1" },
        credentials: "same-origin",
        signal: this._reconcileAbortController.signal
      })

      if (response.status === 204) return
      if (!response.ok) throw new Error(`Reconciliation failed with status ${response.status}`)

      const html = await response.text()
      this._terminalReceived = true
      if (html) Turbo.renderStreamMessage(html)
    } catch (error) {
      if (error.name !== "AbortError") this._terminalReceived = false
    } finally {
      if (!this._terminalReceived) this._reconcileAttempts += 1
      this._reconcileInFlight = false
      this._reconcileAbortController = null
      this._scheduleReconciliation()
    }
  }

  _reconciliationDelay() {
    const delay = this.reconcileIntervalValue * (2 ** this._reconcileAttempts)
    return Math.min(delay, this.reconcileMaxIntervalValue)
  }

  _reconciliationExpired() {
    return Date.now() - this._reconcileStartedAt >= this.reconcileMaxDurationValue
  }

  // Private: observers survive target reconnections within one controller instance
  _observers() {
    this._observerList ||= []
    return this._observerList
  }
}
